<template>
  <section v-if="recognizedOwners.length">
    <p class="mb-2 mt-6 text-left text-neutral-subtle">
      Recognized {{ controllingManager ? 'Controlling Manager' : 'Owners' }}
    </p>
    <div class="flex flex-col gap-3">
      <div
        v-for="owner in orderedRecognizedOwners"
        :key="owner"
        class="flex items-center justify-between gap-2 rounded-lg bg-off-white px-3 py-5"
      >
        <div class="flex w-full items-center justify-between">
          <div class="flex items-center gap-2">
            <IaaoIcon />
            <div class="text-left">
              <p
                v-if="displayUserName(owner)"
                class="text-xs text-neutral-subtle md:text-sm"
              >
                {{ owner?.first_name }} {{ owner?.last_name }}
              </p>
              <p
                :class="[
                  'text-xs md:text-sm',
                  displayUserName(owner)
                    ? 'text-neutral-subtlest'
                    : 'text-neutral-subtle',
                ]"
              >
                {{ formatOwnerEmail(owner) }}
              </p>
              <section class="flex items-center gap-2">
                <p
                  v-if="displayDetails && (owner.job_title || owner.is_type)"
                  class="mt-0.2 text-xs font-normal text-neutral-subtle md:text-sm"
                >
                  {{ ownerJobInfo(owner) }}
                </p>

                <div
                  v-if="shouldShowPercentage(owner, 'mobile')"
                  class="flex items-center gap-1"
                >
                  <img
                    src="@/assets/percentage-icon.svg"
                    alt="Percentage Icon"
                  />
                  <p
                    class="text-xs font-normal text-neutral-subtle-bold md:text-sm"
                  >
                    {{ formattedPercentage(owner.ownership_percentage) }}
                  </p>
                </div>
              </section>
            </div>
          </div>
          <svg
            v-if="
              owner.user_id === store.state.users.currentUser.id &&
              route.path === '/carrier/my-profile' &&
              shouldShowPercentage(owner, 'mobile')
            "
            @click="editOwnership = true"
            class="ml-5 h-6 w-6 cursor-pointer"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 4.39062H6C5.44772 4.39062 5 4.83834 5 5.39062V19.3906C5 19.9429 5.44772 20.3906 6 20.3906H18C18.5523 20.3906 19 19.9429 19 19.3906V12.3906"
              stroke="#6C7C9D"
              stroke-width="2"
            />
            <path
              d="M18.5971 3.53523C18.9876 3.14471 19.6208 3.14471 20.0113 3.53523L20.9212 4.44511C21.3117 4.83564 21.3117 5.4688 20.9212 5.85932L14.0195 12.761C13.8756 12.9049 13.6913 13.0015 13.4911 13.0378L12.3793 13.2398C11.6921 13.3646 11.0919 12.7644 11.2167 12.0772L11.4186 10.9654C11.4549 10.7652 11.5515 10.5808 11.6954 10.4369L18.5971 3.53523Z"
              fill="#6C7C9D"
            />
          </svg>
        </div>

        <div
          v-if="shouldShowPercentage(owner, 'nonMobile')"
          class="flex items-center gap-1"
        >
          <img
            src="@/assets/percentage-icon.svg"
            alt="Percentage Icon"
          />
          <p class="text-xs font-normal text-neutral-subtle-bold md:text-sm">
            {{ formattedPercentage(owner.ownership_percentage) }}
          </p>

          <Popper
            v-if="
              owner.user_id === store.state.users.currentUser.id &&
              route.path === '/carrier/my-profile'
            "
            placement="top"
            :hover="true"
            :arrow="true"
            offset-distance="6"
          >
            <svg
              @click="editOwnership = true"
              class="ml-5 mr-5 h-5 w-5 cursor-pointer hover:rounded-sm hover:bg-light-gray"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 4.39062H6C5.44772 4.39062 5 4.83834 5 5.39062V19.3906C5 19.9429 5.44772 20.3906 6 20.3906H18C18.5523 20.3906 19 19.9429 19 19.3906V12.3906"
                stroke="#6C7C9D"
                stroke-width="2"
              />
              <path
                d="M18.5971 3.53523C18.9876 3.14471 19.6208 3.14471 20.0113 3.53523L20.9212 4.44511C21.3117 4.83564 21.3117 5.4688 20.9212 5.85932L14.0195 12.761C13.8756 12.9049 13.6913 13.0015 13.4911 13.0378L12.3793 13.2398C11.6921 13.3646 11.0919 12.7644 11.2167 12.0772L11.4186 10.9654C11.4549 10.7652 11.5515 10.5808 11.6954 10.4369L18.5971 3.53523Z"
                fill="#6C7C9D"
              />
            </svg>
            <template #content>
              <div
                class="rounded-md bg-dark-gray px-2 py-1 text-left text-2xs text-light-gray"
              >
                <p>Edit</p>
              </div>
            </template>
          </Popper>
        </div>
      </div>
    </div>
  </section>
  <EditOwnershipModal
    :visible="editOwnership"
    :edit-owner="ownershipUser"
    :loading="editOwnershipLoading"
    @close="editOwnership = false"
    @success="updateUser"
  />
</template>

<script setup>
import { useStore } from 'vuex';
import Popper from 'vue3-popper';
import { defineProps, computed, ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import IaaoIcon from '@/components/icons/IaaoIcon.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import EditOwnershipModal from '@/components/modals/EditOwnershipModal.vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const store = useStore();
const toast = useToast();
const { screenSize } = useScreenSize();

const props = defineProps({
  recognizedOwners: {
    type: Array,
    default: () => [],
  },
  displayDetails: {
    type: Boolean,
    default: true,
  },
  controllingManager: {
    type: Boolean,
    default: false,
  },
});

const editOwnership = ref(false);
const editOwnershipLoading = ref(false);

const ownershipUser = computed(() => {
  return (
    store.state.ownership.ownershipData?.ownership_users?.find(
      (owner) => owner.user_id === store.state.users.currentUser.id
    ) || undefined
  );
});

const orderedRecognizedOwners = computed(() => {
  const currentUserId = store.state.users.currentUser.id;
  const [me, others] = props.recognizedOwners.reduce(
    (acc, owner) => {
      if (owner.user_id === currentUserId) acc[0] = owner;
      else acc[1].push(owner);
      return acc;
    },
    [null, []]
  );
  return me ? [me, ...others] : others;
});

function formatOwnerEmail(owner) {
  return owner.user_id === store.state.users.currentUser.id
    ? store.state.users.currentUser.email
    : (owner.invited_user_email ?? '-');
}

function formattedPercentage(percentage) {
  const num = parseFloat(percentage);
  return (num % 1 === 0 ? parseInt(num) : num) + '%';
}

function ownerJobInfo(owner) {
  if (!props.displayDetails) return '';
  if (!owner.job_title && !owner.is_type) return '';

  let result = owner.job_title || '';
  if (owner.job_title && owner.is_type) result += ', ';
  if (owner.is_type) result += `(${formatOwnerType(owner.is_type)})`;

  return result;
}

function getFormattedTimestamp() {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0') +
    ' ' +
    String(now.getHours()).padStart(2, '0') +
    ':' +
    String(now.getMinutes()).padStart(2, '0') +
    ':' +
    String(now.getSeconds()).padStart(2, '0') +
    '.' +
    String(now.getMilliseconds()).padStart(6, '0')
  );
}

async function updateUser({ percentage, jobTitle }) {
  const {
    users: { currentUser },
  } = store.state;

  const ownershipId = ownershipUser.value?.id;
  const companyId = currentUser.company.id;

  const baseBody = {
    ownership_user: {
      details_confirmed_at: getFormattedTimestamp(),
      ownership_percentage: percentage,
      job_title: jobTitle,
    },
  };
  editOwnershipLoading.value = true;

  try {
    await store.dispatch('ownership/UpdateOwnershipUser', {
      ownershipId,
      body: baseBody,
    });

    await store.dispatch(
      'ownership/recordCertification',
      ownershipUser.value.id
    );

    await store.dispatch('ownership/getOwnershipByCompanyId', companyId);

    toast?.open({
      message: 'Ownership details updated successfully',
      position: 'top-right',
      duration: 5000,
    });
  } catch (error) {
    showErrorToast(error);
  } finally {
    editOwnership.value = false;
    editOwnershipLoading.value = false;
  }
}

function showErrorToast(error) {
  toast?.open({
    message:
      error?.response?.data?.[0] ||
      'Something went wrong with your request. Please try again',
    position: 'top-right',
    duration: 5000,
  });
}

function shouldShowPercentage(owner, target) {
  if (!props.displayDetails || !owner.ownership_percentage) return false;
  if (target === 'mobile') return screenSize.value === 'mobile';
  if (target === 'nonMobile') return screenSize.value !== 'mobile';
  return false;
}

function formatOwnerType(type) {
  return type === 'controlling_manager' ? 'Controlling Manager' : 'Owner';
}

const displayUserName = (owner) => {
  return owner.first_name?.length > 0 && owner.last_name?.length > 0;
};
</script>
